<template>
    <div class="flex flex-col h-full w-full p-2" data-cy="orgs-view">
        <UInput
            v-model="searchText"
            type="search"
            placeholder="Søk etter virksomhet"
            data-cy="org-filter"
            class="max-w-1/4 min-w-[300px] my-4"
            icon="i-ph-magnifying-glass"
            @update:model-value="applySearchText"
        />

        <ag-grid-vue
            data-cy="choose-organization"
            class="w-full h-full"
            :theme="agTheme"
            :column-defs="columns"
            :row-data="sortedOrgs"
            :grid-options="gridOptions"
            @keyup.up.exact="prevOrg"
            @keyup.down.exact="nextOrg"
        />
        <org-creator v-if="OrgCreateFeature.isEnabled()"/>
    </div>
</template>

<script lang="ts" setup>
import {computed, onUnmounted, ref, shallowRef, watch} from 'vue'
import {myOrgs, selectedOrg, switchOrg} from '@/tenants/org.service'
import _ from 'lodash'
import {AgGridVue} from 'ag-grid-vue3'
import {Tenant} from '@/tenants/Tenant'
import {ColDef, ColGroupDef, GridApi, GridOptions, GridReadyEvent, ICellRendererParams, IRowNode, RowClassParams, ValueGetterParams} from 'ag-grid-community'
import {ATTACHMENT_UPLOADED, NEXT_ORG, PREV_ORG} from '@/types/events'
import emitter, {useEmitter} from '@/services/infrastructure/emitter'
import {agTheme} from '@/agUtils/ag-theme.service'
import OrgCreator from '@/tenants/OrgCreator.vue'
import {FakeOrgMetrics, OrgCreateFeature, ShowTenantId} from '@/tenants/org.features'
import {getIncomingDocumentsByStatusCount} from '@/services/document.service'
import {TRANSACTION_CREATED} from '@/types/transaction/TransactionEvent'
import {LIST_DOCUMENTS} from '@/task-manager/task.events'
import {globalDefaultColDef} from '@/agUtils'
import {getPendingPaymentCount} from '@/pending-transaction/pendingTransaction.service'
import {PAYMENT_APPROVED, PAYMENT_CREATED, PAYMENT_UPDATED} from '@/bank/bank.events'
import {APPROVED, PENDING} from '@/task-manager/IncomingTaskStatus'

const {onEvents, cleanUp} = useEmitter()
const searchText = ref('')
const gridApi = shallowRef<GridApi>()
const orgMetaData = ref<Record<string, any>>({})

const orgsWithMetadata = computed(() => {
    return myOrgs.value.map((org: Tenant) => {
        return {
            ...org,
            ...orgMetaData.value[org.organizationId],
            isActive: org.organizationId === selectedOrg.value?.organizationId,
            invoicesDueCount: Math.round(Math.random() * 10),
            invoicesDueWeekCount: Math.round(Math.random() * 10),
            incomingEhfCount: Math.round(Math.random() * 10),
            bankReconciledStatus: Math.random() > 0.5,
            vatReportStatus: Math.random() > 0.2,
            financialStatementStatus: Math.random() > 0.8
        }
    })
})

const checkMarkCellRenderer = (isValid: boolean) => {
    if (isValid)
        return '<i class="flex items-center mt-1 text-primary i-ph-check"></i>'
    return '<i class="flex items-center mt-1 text-error i-ph-x"></i>'
}

const columns = computed<(ColDef | ColGroupDef)[]>((): (ColDef | ColGroupDef)[] => [
        {
            headerName: 'Navn',
            field: 'organizationName',
            flex: 1,
            minWidth: 170,
            pinned: 'left'
        },
        {
            headerName: 'Org nr',
            field: 'organizationNumber',
            width: 90,
        },
        {
            headerName: 'tenantId',
            field: 'tenantId',
            width: 240,
            hide: !ShowTenantId.isEnabled(),
            valueGetter: ({data: org}: ValueGetterParams) => {
                const orgId = org.organizationId
                return orgId.substring(0, 10) + '_' + orgId.substring(10)
            }
        },
        {
            headerName: 'Oppgaver',
            children: [
                {
                    headerName: 'Bilag til godkjenning',
                    field: 'incomingDocumentsPendingApprovalCount',
                    width: 150
                },
                {
                    headerName: 'Bilag til bokføring',
                    field: 'pendingVouchersCount',
                    width: 150
                },
                {
                    headerName: 'Betalinger til godkjenning',
                    field: 'pendingPaymentsCount',
                    width: 200
                },
                {
                    headerName: 'Fakt. ff.',
                    field: 'invoicesDueCount',
                    hide: !FakeOrgMetrics.isEnabled(),
                    width: 80
                },
                {
                    headerName: 'Fakt. ff. uke',
                    field: 'invoicesDueWeekCount',
                    hide: !FakeOrgMetrics.isEnabled(),
                    width: 100
                },
                {
                    headerName: 'Innk. EHF',
                    field: 'incomingEhfCount',
                    hide: !FakeOrgMetrics.isEnabled(),
                    width: 100
                },
                {
                    headerName: 'Bank avst.',
                    field: 'bankReconciledStatus',
                    hide: !FakeOrgMetrics.isEnabled(),
                    cellRenderer: ({data}: ICellRendererParams) => checkMarkCellRenderer(data.bankReconciledStatus),
                    width: 100
                },
                {
                    headerName: 'MVA levert.',
                    field: 'vatReportStatus',
                    hide: !FakeOrgMetrics.isEnabled(),
                    cellRenderer: ({data}: ICellRendererParams) => checkMarkCellRenderer(data.vatReportStatus),
                    width: 100
                },
                {
                    headerName: 'Årsregnskap',
                    field: 'financialStatementStatus',
                    hide: !FakeOrgMetrics.isEnabled(),
                    cellRenderer: ({data}: ICellRendererParams) => checkMarkCellRenderer(data.financialStatementStatus),
                    width: 100
                }
            ]
        }
    ]
)

const evaluateSearchFilter = (node: IRowNode) => {
    if (searchText.value === '') return true

    const org = node.data
    return JSON.stringify([org.organizationName, org.organizationNumber])
        .toLowerCase()
        .includes(searchText.value.toLowerCase())
}

const gridOptions: GridOptions = {
    onGridReady: (event: GridReadyEvent) => {
        gridApi.value = event.api
    },
    defaultColDef: {
        ...globalDefaultColDef,
        sortable: true,
        resizable: true
    },
    animateRows: true,
    getRowId: (params: any) => params.data.organizationId,
    rowClass: 'org-grid-row',
    rowClassRules: {
        'active-grid-row': (params: RowClassParams) => {
            return params.data.organizationId === selectedOrg.value?.organizationId
        }
    },
    suppressRowHoverHighlight: true,
    onRowClicked: async (event: any) => {
        const org = event.data
        if (navigator && navigator.clipboard) {
            navigator.clipboard.writeText(org.organizationNumber).then()
        }

        if (org.organizationId !== selectedOrg?.value?.id) {
            switchOrg(org)
        }
    },
        isExternalFilterPresent: () => true,
    doesExternalFilterPass: evaluateSearchFilter
}

const sortedOrgs = computed(() => {
    return _.sortBy(orgsWithMetadata.value, 'organizationName')
})

const applySearchText = () => {
    if (gridApi.value) {
        gridApi.value.onFilterChanged()
    }
}

const fetchIncomingVouchersMetadata = () => {
    const promises: Promise<any>[] = []
    myOrgs.value.forEach((tenant: Tenant) => {
        const countApprovedIncomingDocumentsPromise = getIncomingDocumentsByStatusCount(APPROVED,tenant).then(count => {
            const existingMeta = orgMetaData.value[tenant.organizationId] || {}
            existingMeta['pendingVouchersCount'] = count
            orgMetaData.value[tenant.organizationId] = existingMeta
        })
        promises.push(countApprovedIncomingDocumentsPromise)
        const countIncomingDocumentsPendingApprovalPromise = getIncomingDocumentsByStatusCount(PENDING,tenant).then(count => {
            const existingMeta = orgMetaData.value[tenant.organizationId] || {}
            existingMeta['incomingDocumentsPendingApprovalCount'] = count
            orgMetaData.value[tenant.organizationId] = existingMeta
        })
        promises.push(countIncomingDocumentsPendingApprovalPromise)

        const countPaymentsPromise = getPendingPaymentCount(tenant.organizationId).then(count => {
            const existingMeta = orgMetaData.value[tenant.organizationId] || {}
            existingMeta['pendingPaymentsCount'] = count
            orgMetaData.value[tenant.organizationId] = existingMeta
        })
        promises.push(countPaymentsPromise)
    })
    return Promise.all(promises)
}

watch(myOrgs, fetchIncomingVouchersMetadata, {immediate: true})
onEvents([TRANSACTION_CREATED, LIST_DOCUMENTS, ATTACHMENT_UPLOADED, PAYMENT_CREATED, PAYMENT_APPROVED, PAYMENT_UPDATED], async () => {
    await fetchIncomingVouchersMetadata()
    gridApi.value?.redrawRows()
})

const prevOrg = () => {
    emitter.emit(PREV_ORG)
}

const nextOrg = () => {
    emitter.emit(NEXT_ORG)
}


onUnmounted(cleanUp)

</script>

<style>
@reference "@/index.css";
@reference "tailwindcss";

.org-grid-row {
    @apply hover:bg-base-200 cursor-pointer;

    &.active-grid-row {
        @apply bg-primary/30 hover:bg-primary/30;
    }
}
</style>